/**
 * Auswertung eines Apartments: Ergebnis, Auslastung, Geldfluss, Kanalmix, Ausblick und die
 * Belege dahinter. Der Zeitraum steht in der URL.
 */
import { useParams } from '@tanstack/react-router';
import { useState } from 'react';
import { Button, Card, CardBody, EmptyState, PageHeader } from '@/shared/components';
import { isWorkspaceAdmin, useAuth } from '@/shared/auth';
import { formatDate } from '@/shared/lib/format';
import { CashflowChart } from './CashflowChart';
import { ChannelMixDonut } from './ChannelMixDonut';
import { DrilldownTabs } from './DrilldownTabs';
import { ForecastCard } from './ForecastCard';
import { InsightsKpiHero } from './InsightsKpiHero';
import { PeriodFilter } from './PeriodFilter';
import { StayStream } from './StayStream';
import { usePeriodFilter } from './usePeriodFilter';
import { usePropertyInsights } from './usePropertyInsights';

export function PropertyInsightsPage() {
  const { id: propertyId } = useParams({ from: '/_admin/properties/$id/finance' });
  const { user } = useAuth();
  const { state, setState, periodString } = usePeriodFilter();
  const [activeChannel, setActiveChannel] = useState<string | null>(null);
  const { pnl, expenses, insights, filteredBookings, exportCsv } = usePropertyInsights(
    propertyId,
    periodString,
    state.year,
  );

  if (pnl.isLoading || insights.isLoading) {
    return (
      <p className="text-ink-soft text-sm" aria-busy="true">
        Auswertung wird geladen…
      </p>
    );
  }

  if (pnl.error || !pnl.data || insights.error || !insights.data) {
    return (
      <Card>
        <CardBody>
          <EmptyState
            icon="Error"
            title="Die Auswertung konnte nicht geladen werden"
            description="Bitte später erneut versuchen."
          />
        </CardBody>
      </Card>
    );
  }

  return (
    <>
      <PageHeader
        eyebrow="Apartment-Finanzen"
        title="Ergebnis und"
        titleAccent="Auslastung"
        description={`${formatDate(pnl.data.periodFrom)} – ${formatDate(pnl.data.periodTo)} · Beträge in ${pnl.data.currency}`}
        actions={
          // Der CSV-Export ist Admin-Sache (FinanceExportController) — ohne die Rolle gäbe es
          // hier nur einen Knopf, der zuverlässig in einen 403 läuft.
          isWorkspaceAdmin(user) && (
            <Button
              variant="ghost"
              size="sm"
              className="min-h-11"
              onClick={() => exportCsv.mutate()}
              loading={exportCsv.isPending}
            >
              Jahr als CSV
            </Button>
          )
        }
      />

      {/* Der Zeitraum steht bei den Daten, die er ändert, nicht in der Kopfzeile daneben (§4). */}
      <div className="mb-4">
        <PeriodFilter state={state} onChange={setState} />
      </div>

      <div className="space-y-4">
        <InsightsKpiHero pnl={pnl.data} insights={insights.data} />
        <CashflowChart pnl={pnl.data} />
        <div className="grid gap-4 lg:grid-cols-2">
          <ChannelMixDonut
            insights={insights.data}
            currency={pnl.data.currency}
            activeChannel={activeChannel}
            onChannelChange={setActiveChannel}
          />
          <ForecastCard insights={insights.data} currency={pnl.data.currency} />
        </div>
        <StayStream
          bookings={filteredBookings}
          periodFrom={pnl.data.periodFrom}
          periodTo={pnl.data.periodTo}
          highlightChannel={activeChannel}
        />
        <DrilldownTabs
          bookings={filteredBookings}
          expenses={expenses.data ?? []}
          recurringLines={pnl.data.expenses.recurring.lines}
          currency={pnl.data.currency}
        />
      </div>
    </>
  );
}
